import { useState } from 'react'
import { Link } from 'react-router-dom'
import Button, { ButtonTheme } from 'builder/components/Button'
import { Icon20, Icon24 } from 'builder/components/Icon'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useThemeConfig } from '../../hooks/useThemeConfig'
import { WidgetProps } from '../../widget.types'
import { WidgetTheme } from '../../WidgetTheme'
import { WidgetBox, WidgetImage } from '../../widget.styles'
import { InfoBox } from '../../elements/InfoBox/InfoBox'
import { TRANSLATION } from '../../constants'
import { SuccessBox } from '../../elements/SuccessBox/SuccessBox'
import { ActionButton, ActionText, Box, CloseButton } from './styles'
import JobRecomandationImg from './assets/job-recomandation.png'
import JobRecomandationMobileImg from './assets/job-recomandation-mobile.png'

import Banner from './assets/banner.png'

const localStorageKey = 'jobs_applied_banner'

function useJobsAppliedBanner() {
  const [jobsAppliedBanner, setJobsAppliedBanner] = useState(() => {
    const stored = localStorage.getItem(localStorageKey)
    return stored ? JSON.parse(stored) : true
  })

  const toggleBanner = () => {
    const newValue = !jobsAppliedBanner
    setJobsAppliedBanner(newValue)
    localStorage.setItem(localStorageKey, JSON.stringify(newValue))
  }

  return { jobsAppliedBanner, toggleBanner }
}

function InitialJobApplied(props: {
  toggleBanner: () => void
  handleAutoApplyClick: (e: React.MouseEvent) => void
  text: {
    badge?: string
    header: string
    subHeader: string
    applyForMe: string
    notNow: string
  }
}) {
  return (
    <>
      <CloseButton onClick={props.toggleBanner}>
        <Icon24.Close />
      </CloseButton>
      <WidgetBox>
        <InfoBox text={props.text}>
          <Box display="flex" gap="12px" alignItems="flex-end">
            <Button
              as={Link}
              to="/auto-apply/start"
              theme={ButtonTheme.alternative}
              onClick={props.handleAutoApplyClick}
            >
              {props.text.applyForMe}
            </Button>
            <Button theme={ButtonTheme.ghost} onClick={props.toggleBanner}>
              {props.text.notNow}
            </Button>
          </Box>
        </InfoBox>
      </WidgetBox>
      <WidgetBox>
        <WidgetImage src={Banner} alt="Banner" />
      </WidgetBox>
    </>
  )
}

function ProgressJobApplied(props: {
  handleAutoApplyClick: (e: React.MouseEvent) => void
  text: {
    badge?: string
    header: string
    subHeader: string
    applyForMe: string
    exploreJobs: string
  }
}) {
  const { isPhone } = useMediaQueries()
  return (
    <>
      <WidgetBox>
        <InfoBox text={props.text}>
          <Box display="flex" gap="4px" flexDirection="column" directionOnMobile="row">
            <ActionButton
              as={Link}
              to="/job-search?view=recommendation"
              style={{ background: 'white', display: 'block', padding: 12 }}
              theme={ButtonTheme.ghost}
            >
              <ActionText>
                {props.text.exploreJobs}
                <Icon20.ChevronRightRegular />
              </ActionText>
            </ActionButton>
            <ActionButton
              as={Link}
              to="/auto-apply/start"
              style={{ background: 'white', display: 'block', padding: 12 }}
              theme={ButtonTheme.ghost}
              onClick={props.handleAutoApplyClick}
            >
              <ActionText>
                {props.text.applyForMe}
                <Icon20.ChevronRightRegular />
              </ActionText>
            </ActionButton>
          </Box>
        </InfoBox>
      </WidgetBox>
      <WidgetBox>
        <WidgetImage
          src={isPhone ? JobRecomandationMobileImg : JobRecomandationImg}
          style={{ margin: 'auto' }}
        />
      </WidgetBox>
    </>
  )
}

/**
 * This widget is used to show the Jobs Applied section in the dashboard.
 * ATM is not complete since it needs another success screen state, but that
 * will be implmented once the backend is ready.
 */
export const JobApplied = ({ service }: WidgetProps) => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()

  const autoApply = useAutoApply()
  const themeState = useThemeConfig({ service })
  const { jobsAppliedBanner, toggleBanner } = useJobsAppliedBanner()
  const theme = themeState.initial

  const initialText = {
    badge: i18n.t(`${TRANSLATION}.job_applied.initial.badge`),
    header: i18n.t(`${TRANSLATION}.job_applied.initial.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.initial.sub_header`),
    applyForMe: i18n.t(`${TRANSLATION}.job_applied.initial.apply_for_me`),
    notNow: i18n.t(`${TRANSLATION}.job_applied.initial.not_now`),
  }

  const progressText = {
    badge: i18n.t(`${TRANSLATION}.job_applied.progress.badge`),
    header: i18n.t(`${TRANSLATION}.job_applied.progress.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.progress.sub_header`),
    applyForMe: i18n.t(`${TRANSLATION}.job_applied.progress.apply_for_me`),
    exploreJobs: isPhone
      ? i18n.t(`${TRANSLATION}.job_applied.progress.explore_jobs_mobile`)
      : i18n.t(`${TRANSLATION}.job_applied.progress.explore_jobs`),
  }

  const completedText = {
    header: i18n.t(`${TRANSLATION}.job_applied.completed.header`),
    subHeader: i18n.t(`${TRANSLATION}.job_applied.completed.sub_header`),
    actionText: i18n.t(`${TRANSLATION}.job_applied.completed.action_text`),
  }

  const handleAutoApplyClick = (e: React.MouseEvent) => {
    if (!autoApply.isUserOnAutoApplyPlan) {
      e.preventDefault()
      autoApply.redirectToBillingPlans()
    }
  }

  if (service.progress === 100) {
    return (
      <WidgetTheme theme={themeState.completed} tipOrder={0}>
        <WidgetBox>
          <InfoBox text={completedText} isCompleted={true}>
            <ActionButton as={Link} to="#">
              {completedText.actionText}
            </ActionButton>
          </InfoBox>
        </WidgetBox>
        <WidgetBox>
          <SuccessBox />
        </WidgetBox>
      </WidgetTheme>
    )
  }

  return (
    <WidgetTheme theme={theme} tipOrder={0}>
      {jobsAppliedBanner ? (
        <InitialJobApplied
          text={initialText}
          toggleBanner={toggleBanner}
          handleAutoApplyClick={handleAutoApplyClick}
        />
      ) : (
        <ProgressJobApplied handleAutoApplyClick={handleAutoApplyClick} text={progressText} />
      )}
    </WidgetTheme>
  )
}
